import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Refresh, ArrowBack, GetApp } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';

const PRIMARY = '#1c2266';
const PRIMARY_MID = '#2d35a0';
const PRIMARY_LT = '#eef0ff';

const fmtV = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt2Date = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const iconBtn = {
    border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
    height: 28, width: 28, color: '#fff', flexShrink: 0,
    '&:hover': { background: 'rgba(255,255,255,0.1)' },
    '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
};

const GSTR3BReport = () => {
    useSessionValidation();
    const { companyId, branchId, token, companyName, username } = useAuth();
    const { logAudit } = useAuditLog();
    const history = useHistory();

    const today = new Date();
    const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
    const [toDate, setToDate] = useState(fmt2Date(today));
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [fetched, setFetched] = useState(false);

    useEffect(() => {
        if (companyId && branchId && token && fromDate && toDate) {
      if (new Date(fromDate) > new Date(toDate)) {
        setData([]);
        setFetched(false);
        setError('From Date cannot be greater than To Date');
        return;
      }
      fetchReport();
    }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, branchId, token]);

    const fetchReport = async () => {
        if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
        setLoading(true);
        try {
            const res = await fetchData('GSTReportApi', `/GSTR3BDetails/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
            const result = res?.response_data || res || [];
            setData(Array.isArray(result) ? result[0] || null : result);
            setFetched(true);
        } catch (err) {
            console.error(err);
            setError('Failed to fetch GSTR 3B report');
        } finally { setLoading(false); }
    };

    const handleExport = async () => {
        logAudit(AUDIT_ACTION.EXPORT, 'GSTR3BReport', 0);
        if (!data) return;
        const headers = ['Description', 'Taxable Amount', 'CGST', 'SGST', 'IGST'];
        const rows = [
            ['Outward Supplies', data.OutputTaxable, data.OutputCGST, data.OutputSGST, data.OutputIGST],
            ['Input Tax Credit', data.ITCTaxable, data.ITCCGST, data.ITCSGST, data.ITCIGST],
            ['Net CGST Payable', '', data.NetCGSTPayable, '', ''],
            ['Net SGST Payable', '', '', data.NetSGSTPayable, ''],
            ['Net IGST Payable', '', '', '', data.NetIGSTPayable],
            ['Total GST Payable', '', '', '', data.TotalGSTPayable],
        ];
        await exportStyledExcel({
            reportTitle: 'GSTR 3B REPORT', sheetName: 'GSTR3B',
            fileName: `GSTR3B_${fromDate}_${toDate}.xlsx`,
            companyName, username, fromDate, toDate, headers, rows,
            totalsRow: ['Total GST Payable', '', '', '', fmtV(data.TotalGSTPayable)],
            columnWidths: [24, 16, 14, 14, 14],
        });
    };

    const th = { padding: '4px 8px', fontWeight: 600, fontSize: 10, color: '#fff', background: PRIMARY_MID, borderBottom: '1px solid #e2e8f0', textAlign: 'right', whiteSpace: 'nowrap' };
    const td = { padding: '4px 8px', fontSize: 11, borderBottom: '1px solid #f1f5f9', textAlign: 'right' };
    const tdL = { ...td, textAlign: 'left', fontWeight: 600, color: '#334155' };

    return (
        <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 8, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
            <LogoLoader open={loading} message="Loading GSTR 3B..." />
            <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
            </Snackbar>

            <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                {/* Toolbar */}
                <div style={{ display: 'flex', alignItems: 'center', padding: '5px 10px', background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_MID} 100%)`, gap: 5, flexWrap: 'nowrap', overflowX: 'auto', scrollbarWidth: 'none' }}>
                    <Tooltip title="Back" arrow><IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}><ArrowBack sx={{ fontSize: 15 }} /></IconButton></Tooltip>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' }}>GSTR 3B</span>
                    <div style={{ flex: 1 }} />
                    <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={{ height: 26, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 6, padding: '0 6px', fontSize: 11, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} style={{ height: 26, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 6, padding: '0 6px', fontSize: 11, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <Tooltip title="Refresh" arrow><IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}><Refresh sx={{ fontSize: 15 }} /></IconButton></Tooltip>
                    <Tooltip title="Export" arrow><IconButton onClick={handleExport} disabled={!data} size="small" sx={iconBtn}><GetApp sx={{ fontSize: 15 }} /></IconButton></Tooltip>
                </div>

                {/* Body */}
                <div style={{ padding: '8px 10px' }}>
                    {!fetched && !loading && <div style={{ textAlign: 'center', padding: 28, color: '#94a3b8', fontSize: 12 }}>Select date range and click Refresh</div>}
                    {fetched && !data && <div style={{ textAlign: 'center', padding: 28, color: '#94a3b8', fontSize: 12 }}>No data found</div>}

                    {fetched && data && (
                        <>
                            {/* Summary strip */}
                            <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
                                {[
                                    { label: 'Output Tax', value: `₹${fmtV((parseFloat(data.OutputCGST)||0)+(parseFloat(data.OutputSGST)||0)+(parseFloat(data.OutputIGST)||0))}`, color: '#dc2626' },
                                    { label: 'ITC', value: `₹${fmtV((parseFloat(data.ITCCGST)||0)+(parseFloat(data.ITCSGST)||0)+(parseFloat(data.ITCIGST)||0))}`, color: '#059669' },
                                    { label: 'Net Payable', value: `₹${fmtV(data.TotalGSTPayable)}`, color: PRIMARY },
                                ].map(s => (
                                    <div key={s.label} style={{ background: PRIMARY_LT, borderRadius: 6, padding: '5px 14px', display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 100 }}>
                                        <span style={{ fontSize: 9, color: '#64748b', fontWeight: 600 }}>{s.label}</span>
                                        <span style={{ fontSize: 12, color: s.color, fontWeight: 700 }}>{s.value}</span>
                                    </div>
                                ))}
                            </div>

                            {/* 3.1 Outward Supplies */}
                            <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden', marginBottom: 8 }}>
                                <div style={{ padding: '4px 10px', background: '#1e40af', color: '#fff', fontSize: 10, fontWeight: 700 }}>3.1 - Outward Supplies</div>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead><tr>
                                        <th style={{ ...th, textAlign: 'left' }}>Description</th>
                                        <th style={th}>Taxable</th><th style={th}>CGST</th><th style={th}>SGST</th><th style={th}>IGST</th>
                                    </tr></thead>
                                    <tbody>
                                        <tr>
                                            <td style={tdL}>Outward Taxable Supplies</td>
                                            <td style={td}>{fmtV(data.OutputTaxable)}</td>
                                            <td style={td}>{fmtV(data.OutputCGST)}</td>
                                            <td style={td}>{fmtV(data.OutputSGST)}</td>
                                            <td style={td}>{fmtV(data.OutputIGST)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            {/* 4. ITC */}
                            <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden', marginBottom: 8 }}>
                                <div style={{ padding: '4px 10px', background: '#059669', color: '#fff', fontSize: 10, fontWeight: 700 }}>4. Input Tax Credit</div>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead><tr>
                                        <th style={{ ...th, textAlign: 'left', background: '#047857' }}>Description</th>
                                        <th style={{ ...th, background: '#047857' }}>Taxable</th><th style={{ ...th, background: '#047857' }}>CGST</th><th style={{ ...th, background: '#047857' }}>SGST</th><th style={{ ...th, background: '#047857' }}>IGST</th>
                                    </tr></thead>
                                    <tbody>
                                        <tr>
                                            <td style={tdL}>ITC Available</td>
                                            <td style={td}>{fmtV(data.ITCTaxable)}</td>
                                            <td style={{ ...td, color: '#059669' }}>{fmtV(data.ITCCGST)}</td>
                                            <td style={{ ...td, color: '#059669' }}>{fmtV(data.ITCSGST)}</td>
                                            <td style={{ ...td, color: '#059669' }}>{fmtV(data.ITCIGST)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            {/* 6. Net Liability */}
                            <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden' }}>
                                <div style={{ padding: '4px 10px', background: PRIMARY, color: '#fff', fontSize: 10, fontWeight: 700 }}>6. Payment of Tax</div>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead><tr>
                                        <th style={{ ...th, textAlign: 'left' }}>Tax</th>
                                        <th style={th}>Output</th><th style={th}>ITC</th><th style={th}>Net Payable</th>
                                    </tr></thead>
                                    <tbody>
                                        <tr><td style={tdL}>CGST</td><td style={td}>{fmtV(data.OutputCGST)}</td><td style={{ ...td, color: '#059669' }}>{fmtV(data.ITCCGST)}</td><td style={{ ...td, fontWeight: 700, color: PRIMARY }}>{fmtV(data.NetCGSTPayable)}</td></tr>
                                        <tr style={{ background: '#fafafa' }}><td style={tdL}>SGST</td><td style={td}>{fmtV(data.OutputSGST)}</td><td style={{ ...td, color: '#059669' }}>{fmtV(data.ITCSGST)}</td><td style={{ ...td, fontWeight: 700, color: PRIMARY }}>{fmtV(data.NetSGSTPayable)}</td></tr>
                                        <tr><td style={tdL}>IGST</td><td style={td}>{fmtV(data.OutputIGST)}</td><td style={{ ...td, color: '#059669' }}>{fmtV(data.ITCIGST)}</td><td style={{ ...td, fontWeight: 700, color: PRIMARY }}>{fmtV(data.NetIGSTPayable)}</td></tr>
                                        <tr style={{ background: PRIMARY_LT }}>
                                            <td style={{ ...tdL, fontWeight: 700, color: PRIMARY }}>Total</td>
                                            <td style={{ ...td, fontWeight: 700 }}>{fmtV((parseFloat(data.OutputCGST)||0)+(parseFloat(data.OutputSGST)||0)+(parseFloat(data.OutputIGST)||0))}</td>
                                            <td style={{ ...td, fontWeight: 700, color: '#059669' }}>{fmtV((parseFloat(data.ITCCGST)||0)+(parseFloat(data.ITCSGST)||0)+(parseFloat(data.ITCIGST)||0))}</td>
                                            <td style={{ ...td, fontWeight: 800, color: '#dc2626', fontSize: 12 }}>₹{fmtV(data.TotalGSTPayable)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default GSTR3BReport;
